/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


/**
 * Client-side price filtering for catalog-adjusted prices.
 *
 * Shopify's server-side price filter uses base variant prices, but B2B catalog
 * pricing changes the prices customers actually see. This code filters
 * client-side using catalog_price_gte/lte URL params instead of filter.v.price.*
 *
 * Key integration points with the theme's filter system:
 * - Intercepts facet:criteria-changed to preserve catalog price params across
 *   AJAX re-renders (theme's FacetFilters only knows about filter.* inputs)
 * - Restores slider state after DOM replacement with a delay to let
 *   PriceRange.connectedCallback finish binding first
 * - Uses debounced MutationObserver to avoid cascading re-applies
 */
(function () {
  var currencySymbol = '';
  var isRestoring = false;

  function getCurrencySymbol() {
    if (currencySymbol) return currencySymbol;
    var el = document.querySelector('.price-range .input-prefix__value');
    if (el) currencySymbol = el.textContent.trim();
    return currencySymbol;
  }

  function getSelectedPrices() {
    var params = new URLSearchParams(window.location.search);
    var min = params.get('catalog_price_gte');
    var max = params.get('catalog_price_lte');
    return {
      min: min ? parseInt(min) : null,
      max: max ? parseInt(max) : null
    };
  }

  function getSliderMax() {
    var priceRange = document.querySelector('price-range');
    if (!priceRange) return 0;
    var ranges = priceRange.querySelectorAll('.price-range__range-group input[type="range"]');
    return ranges.length >= 2 ? parseInt(ranges[1].max) : 0;
  }

  function filterProducts() {
    var sel = getSelectedPrices();
    var items = document.querySelectorAll('product-item[data-product-price]');
    if (!items.length) return;

    var hasFilter = sel.min !== null || sel.max !== null;
    var visibleCount = 0;

    items.forEach(function (item) {
      if (!hasFilter) {
        item.style.display = '';
        visibleCount++;
        return;
      }
      var priceCents = parseInt(item.getAttribute('data-product-price'));
      var priceWhole = Math.ceil(priceCents / 100);
      var minWhole = sel.min || 0;
      var maxWhole = sel.max || Infinity;

      if (priceWhole >= minWhole && priceWhole <= maxWhole) {
        item.style.display = '';
        visibleCount++;
      } else {
        item.style.display = 'none';
      }
    });

    if (hasFilter) {
      var countEl = document.querySelector('.product-facet__meta-bar-item--count');
      if (countEl) {
        countEl.setAttribute('data-original-text', countEl.getAttribute('data-original-text') || countEl.textContent);
        countEl.textContent = countEl.textContent.replace(/\d+/, visibleCount);
      }
    }
  }

  /**
   * Restore slider inputs to match URL state after AJAX re-render.
   * Must run AFTER PriceRange.connectedCallback has bound its listeners,
   * so we use a short delay.
   */
  function restoreSlider() {
    var sel = getSelectedPrices();
    if (sel.min === null && sel.max === null) return;

    var priceRange = document.querySelector('price-range');
    if (!priceRange) return;

    var ranges = priceRange.querySelectorAll('.price-range__range-group input[type="range"]');
    var textInputs = priceRange.querySelectorAll('.price-range__input input[type="number"]');
    if (ranges.length < 2 || textInputs.length < 2) return;

    isRestoring = true;
    var sliderMax = parseInt(ranges[1].max);

    if (sel.min !== null) {
      var minVal = Math.min(sel.min, sliderMax);
      ranges[0].value = minVal;
      textInputs[0].value = minVal;
    }
    if (sel.max !== null) {
      var maxVal = Math.min(sel.max, sliderMax);
      ranges[1].value = maxVal;
      textInputs[1].value = maxVal;
    }

    var lo = parseInt(ranges[0].value);
    var hi = parseInt(ranges[1].value);
    var mx = parseInt(ranges[0].max);
    if (mx > 0) {
      ranges[0].parentElement.style.setProperty('--range-min', (lo / mx * 100) + '%');
      ranges[0].parentElement.style.setProperty('--range-max', (hi / mx * 100) + '%');
    }
    isRestoring = false;
  }

  function updatePriceUI() {
    var sel = getSelectedPrices();
    var sym = getCurrencySymbol();
    var sliderMax = getSliderMax();

    var label = document.querySelector('[data-catalog-price-label]');
    if (label) {
      if (sel.min !== null || sel.max !== null) {
        label.textContent = sym + (sel.min || 0) + ' - ' + sym + (sel.max || sliderMax);
        label.style.display = '';
      } else {
        label.style.display = 'none';
        label.textContent = '';
      }
    }

    managePriceActiveTag(sel, sym, sliderMax);
  }

  function managePriceActiveTag(sel, sym, sliderMax) {
    document.querySelectorAll('.tag[data-catalog-price-tag]').forEach(function (el) {
      el.remove();
    });

    var activeLists = document.querySelectorAll('.product-facet__active-list');
    var hasActivePrice = sel.min !== null || sel.max !== null;

    if (!hasActivePrice) {
      activeLists.forEach(function (list) {
        if (!list.querySelector('.tag')) {
          list.style.display = 'none';
        }
      });
      return;
    }

    var minText = sym + (sel.min || 0);
    var maxText = sym + (sel.max || sliderMax);

    var clearUrl = new URL(window.location);
    clearUrl.searchParams.delete('catalog_price_gte');
    clearUrl.searchParams.delete('catalog_price_lte');

    var tagHtml = '<div class="tag" data-catalog-price-tag>' +
      '<a href="' + clearUrl.pathname + clearUrl.search + '" data-action="clear-catalog-price" class="tag__icon tap-area" aria-label="Delete">' +
      '<svg focusable="false" width="10" height="10" class="icon icon--close" viewBox="0 0 14 14"><path d="M13 1L1 13M1 1l12 12" stroke="currentColor" stroke-width="2" fill="none"/></svg>' +
      '</a>' +
      minText + ' - ' + maxText +
      '</div>';

    activeLists.forEach(function (list) {
      list.style.display = '';
      list.insertAdjacentHTML('afterbegin', tagHtml);
    });
  }

  function onPriceInputChange() {
    if (isRestoring) return;

    var priceRange = document.querySelector('price-range');
    if (!priceRange) return;

    var textInputs = priceRange.querySelectorAll('.price-range__input input[type="number"]');
    if (textInputs.length < 2) return;

    var minVal = textInputs[0].value ? parseInt(textInputs[0].value) : null;
    var maxVal = textInputs[1].value ? parseInt(textInputs[1].value) : null;
    var sliderMax = getSliderMax();

    if (minVal === 0) minVal = null;
    if (maxVal !== null && maxVal >= sliderMax) maxVal = null;

    var url = new URL(window.location);
    if (minVal) {
      url.searchParams.set('catalog_price_gte', minVal);
    } else {
      url.searchParams.delete('catalog_price_gte');
    }
    if (maxVal) {
      url.searchParams.set('catalog_price_lte', maxVal);
    } else {
      url.searchParams.delete('catalog_price_lte');
    }
    history.replaceState({}, '', url.toString());

    filterProducts();
    updatePriceUI();
  }

  /** Listen for price input changes (delegated, survives DOM replacement) */
  document.addEventListener('change', function (e) {
    if (e.target.name === 'catalog_price_gte' || e.target.name === 'catalog_price_lte') {
      onPriceInputChange();
    }
  });

  /** Handle clear price tag click (delegated, survives DOM replacement) */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-action="clear-catalog-price"]');
    if (!link) return;
    e.preventDefault();

    var url = new URL(window.location);
    url.searchParams.delete('catalog_price_gte');
    url.searchParams.delete('catalog_price_lte');
    history.replaceState({}, '', url.toString());

    var priceRange = document.querySelector('price-range');
    if (priceRange) {
      var ranges = priceRange.querySelectorAll('.price-range__range-group input[type="range"]');
      var textInputs = priceRange.querySelectorAll('.price-range__input input[type="number"]');
      if (ranges.length >= 2) {
        var mx = parseInt(ranges[0].max);
        ranges[0].value = 0;
        ranges[1].value = mx;
        textInputs[0].value = '';
        textInputs[1].value = '';
        ranges[0].parentElement.style.setProperty('--range-min', '0%');
        ranges[0].parentElement.style.setProperty('--range-max', '100%');
      }
    }

    filterProducts();
    updatePriceUI();

    var countEl = document.querySelector('.product-facet__meta-bar-item--count');
    if (countEl && countEl.getAttribute('data-original-text')) {
      countEl.textContent = countEl.getAttribute('data-original-text');
    }
  });

  /**
   * Intercept facet:criteria-changed to preserve catalog price params.
   *
   * When the theme's FacetFilters fires this event (e.g. user changes a
   * non-price filter), it builds the URL purely from FormData which does
   * not include our catalog_price params. We intercept the event, inject
   * our params into the URL before ProductFacet._rerender processes it.
   */
  document.addEventListener('facet:criteria-changed', function (e) {
    var sel = getSelectedPrices();
    if (sel.min === null && sel.max === null) return;
    if (!e.detail || !e.detail.url) return;

    var url = new URL(e.detail.url, window.location.origin);
    if (sel.min !== null) url.searchParams.set('catalog_price_gte', sel.min);
    if (sel.max !== null) url.searchParams.set('catalog_price_lte', sel.max);
    e.detail.url = url.toString();
  }, true);

  /** Strip legacy filter.v.price.* params */
  var url = new URL(window.location);
  var modified = false;
  Array.from(url.searchParams.keys()).forEach(function (key) {
    if (key.indexOf('filter.v.price') === 0) {
      url.searchParams.delete(key);
      modified = true;
    }
  });
  if (modified) {
    history.replaceState({}, '', url.toString());
  }

  // Initial apply
  filterProducts();
  restoreSlider();
  updatePriceUI();

  // After AJAX re-renders, re-apply. Only watch #facet-main for childList
  // changes (product grid replacement), not subtree, to avoid loops from
  // our own DOM modifications (tag injection, slider value changes).
  var facetMain = document.querySelector('#facet-main');
  var facetFiltersEl = document.querySelector('#facet-filters');
  if (facetMain || facetFiltersEl) {
    var debounceTimer = null;
    var observerCallback = function () {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        restoreSlider();
        filterProducts();
        updatePriceUI();
      }, 100);
    };
    var observer = new MutationObserver(observerCallback);
    // Watch for innerHTML replacement on these containers (childList only, no subtree)
    if (facetMain) observer.observe(facetMain, { childList: true });
    if (facetFiltersEl) observer.observe(facetFiltersEl, { childList: true });
  }
})();


/**
 * Metafield-based quantity enforcement for cart quantity inputs (minimum order
 * quantity and pack size).
 *
 * Native B2B `quantity_rule` constraints are enforced by Shopify server-side and
 * take priority. Only when a native value is left at its default does Liquid fall
 * back to the product metafield — and in that case it renders `data-effective-min`
 * (the floor) and `data-pack-size` (the step) on the input, which is what these
 * helpers act on. The decrease anchor href is rendered with `at_least:
 * effective_min` and steps by the effective increment in Liquid.
 *
 * This interceptor guards the AJAX path: if a decrease click would set quantity
 * below the effective minimum (but above 0), it clamps to that minimum. Quantity 0
 * is allowed as it triggers item removal (Shopify convention).
 */
(function () {
  document.addEventListener('click', function (event) {
    var anchor = event.target.closest('line-item-quantity .quantity-selector__button[href*="cart/change"]');
    if (!anchor) return;

    var href = anchor.getAttribute('href');
    if (!href) return;

    var url = new URL(href, window.location.origin);
    var quantity = parseInt(url.searchParams.get('quantity'), 10);
    if (isNaN(quantity)) return;

    var lineItemQuantity = anchor.closest('line-item-quantity');
    if (!lineItemQuantity) return;

    var input = lineItemQuantity.querySelector('input[data-effective-min]');
    if (!input) return;

    var effectiveMin = parseInt(input.getAttribute('data-effective-min'), 10);
    if (isNaN(effectiveMin) || effectiveMin <= 1) return;

    // Allow quantity 0 (item removal), but clamp anything between 1 and min-1 up to the minimum
    if (quantity > 0 && quantity < effectiveMin) {
      event.preventDefault();
      event.stopImmediatePropagation();
      url.searchParams.set('quantity', effectiveMin);
      anchor.setAttribute('href', url.pathname + url.search);
      anchor.click();
    }
  }, true);

  /**
   * Auto-correct cart items that don't conform to their metafield quantity
   * constraints (e.g. added before MOQ or pack size was set on the product).
   *
   * For each input carrying metafield rules, a quantity is valid when it is
   * `effectiveMin + n*packSize` (n >= 0). Anything else is rounded UP to the
   * nearest valid value. `packSize` of 1 (MOQ-only) reduces this to simply
   * raising below-minimum quantities up to the minimum.
   */
  var cartCorrectionRan = false;

  function correctNonConformingCartItems() {
    if (cartCorrectionRan) return;

    var inputs = document.querySelectorAll('line-item-quantity input[data-effective-min]');
    var corrections = [];

    inputs.forEach(function (input) {
      var line = input.getAttribute('data-line');
      var currentQty = parseInt(input.value, 10);
      if (!line || isNaN(currentQty)) return;

      var effectiveMin = parseInt(input.getAttribute('data-effective-min'), 10);
      if (isNaN(effectiveMin)) return;

      var packSize = parseInt(input.getAttribute('data-pack-size'), 10);
      if (isNaN(packSize) || packSize < 1) packSize = 1;

      var corrected;
      if (currentQty <= effectiveMin) {
        corrected = effectiveMin;
      } else {
        var steps = Math.ceil((currentQty - effectiveMin) / packSize);
        corrected = effectiveMin + steps * packSize;
      }

      if (corrected === currentQty) return;

      corrections.push({ line: parseInt(line, 10), quantity: corrected });
    });

    if (corrections.length === 0) return;
    cartCorrectionRan = true;

    // Process corrections sequentially then refresh cart once
    var chain = Promise.resolve();
    corrections.forEach(function (c) {
      chain = chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(c)
        });
      });
    });

    chain.then(function () {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', correctNonConformingCartItems);
  } else {
    correctNonConformingCartItems();
  }
})();

/**
 * Expand product facet filters by default on load.
 * Guarded reimplementation of an admin-added snippet (a markup/CSS-level
 * default would avoid the open-on-load flash - worth revisiting).
 */
(function () {
  function expandProductFacetFilters() {
    document.querySelectorAll('.product-facet__filter-item').forEach(function (filter) {
      var button = filter.querySelector('button');
      var content = filter.querySelector('collapsible-content');
      if (button) button.setAttribute('aria-expanded', 'true');
      if (content) content.style.overflow = 'visible';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', expandProductFacetFilters);
  } else {
    expandProductFacetFilters();
  }
})();


